import rdiff from 'recursive-diff'

import {
    PrepCheck,
    JourneyPhase,
    PrepCheckOverview,
    SupportedHazardFilter,
    LanguageCode,
    PrepCheckOverviewDTO,
    PrepCheckDTO,
} from '../entities'

/**
 * Takes an old and new state of a prep check object and replaces old values with new values
 * @param currentPrepCheck
 * @param latestPrepCheck
 * @returns PrepCheck
 */
export function updatePrepCheck(
    currentPrepCheck: PrepCheck,
    latestPrepCheck: PrepCheck
): PrepCheck {
    if (currentPrepCheck.id !== latestPrepCheck.id) {
        return currentPrepCheck
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(
        currentPrepCheck,
        latestPrepCheck
    )
    const updatedHazard: PrepCheck = rdiff.applyDiff(currentPrepCheck, diff)

    return updatedHazard
}

/**
 * Takes an old and new state of a journey phase object and replaces old values with new values
 * @param currentJourneyPhase
 * @param latestJourneyPhase
 * @returns JourneyPhase
 */
export function updateJourneyPhase(
    currentJourneyPhase: JourneyPhase,
    latestJourneyPhase: JourneyPhase
): JourneyPhase {
    if (currentJourneyPhase.id !== latestJourneyPhase.id) {
        return currentJourneyPhase
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(
        currentJourneyPhase,
        latestJourneyPhase
    )
    const updatedHazard: JourneyPhase = rdiff.applyDiff(
        currentJourneyPhase,
        diff
    )

    return updatedHazard
}

/**
 * Sort prep checks
 * @param prepChecks the list of prep check overviews to sort
 * @param activeFilters the list of active filters
 * @returns PrepCheckOverviewDTO[]
 */
export const sortPrepChecks = (
    prepChecks: PrepCheckOverviewDTO[],
    activeFilters: SupportedHazardFilter[]
): PrepCheckOverviewDTO[] => {
    const prep_checks = [...prepChecks]
    // if no active filters, just return prep checks in alphabetical order
    if (activeFilters.length === 0) {
        return prep_checks.sort((a, b) => (a.title < b.title ? -1 : 1))
    }

    return prep_checks.sort((a, b) => {
        // get active filters in A
        const intersectionA: SupportedHazardFilter[] = a.filters.filter((t) =>
            activeFilters.includes(t)
        )

        // get active filters in B
        const intersectionB: SupportedHazardFilter[] = b.filters.filter((t) =>
            activeFilters.includes(t)
        )
        if (intersectionA.length !== intersectionB.length) {
            // put prep check with more matching filters in front
            return intersectionB.length - intersectionA.length
        } else {
            // put prep checks with more recently-turned-on filters in front
            for (const filter of activeFilters) {
                const filterInA = intersectionA.includes(filter)
                const filterInB = intersectionB.includes(filter)
                if (filterInA && !filterInB) {
                    return 1
                } else if (!filterInA && filterInB) {
                    return -1
                }
            }
            // if filters are a total match, sort in alphabetical order
            return a.title < b.title ? -1 : 1
        }
    })
}

/**
 * stripLanguagesFromPrepCheck
 * @param prep_check The prep check to filter.
 * @param language The language to return the prep check in
 * @returns PrepCheckDTO
 */
export const stripLanguagesFromPrepCheck = (
    prep_check: PrepCheck,
    language: LanguageCode = LanguageCode.ENGLISH
): PrepCheckDTO => {
    return {
        ...prep_check,
        title: prep_check.title[language],
        questions: prep_check.questions.map((q) => ({
            ...q,
            name: q.name[language],
            question: q.question[language],
            subheading: q.subheading ? q.subheading[language] : undefined,
            explainer_text: q.explainer_text
                ? q.explainer_text[language]
                : undefined,
            options: q.options.map((o) => ({
                ...o,
                text: o.text[language],
                explainer_text: o.explainer_text
                    ? o.explainer_text[language]
                    : undefined,
                image: o.image
                    ? {
                          ...o.image,
                          alt: o.image.alt[language],
                      }
                    : undefined,
            })),
            image: q.image
                ? {
                      ...q.image,
                      alt: q.image.alt[language],
                  }
                : undefined,
        })),
        dark_icon: prep_check.dark_icon
            ? {
                  ...prep_check.dark_icon,
                  alt: prep_check.dark_icon.alt[language],
              }
            : undefined,
        light_icon: prep_check.light_icon
            ? {
                  ...prep_check.light_icon,
                  alt: prep_check.light_icon.alt[language],
              }
            : undefined,
    }
}
